import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MessageSquare, Plus, Sparkles, Clock, FileText, Calendar, UserCheck, GitBranch, StickyNote } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Card, Badge, RedButton, GhostButton, Field, inputCls } from "../../components/crm/ui";
import { toast } from "sonner";

const ICONS = { "Phone Call": Phone, Text: MessageSquare, Email: Mail, Note: StickyNote, "Form Submission": FileText, "Event Attendance": Calendar, "Pipeline Change": GitBranch, "Assignment Change": UserCheck, Outreach: UserCheck };

export default function ContactDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [note, setNote] = useState("");
  const [summary, setSummary] = useState("");
  const [loadingAi, setLoadingAi] = useState(false);
  const [showTask, setShowTask] = useState(false);

  const load = () => api.get(`/contacts/${id}`).then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); api.get("/pipelines").then(({ data }) => setPipeline(data.items[0])).catch(() => {}); }, [id]);

  const addNote = async () => { if (!note.trim()) return; await api.post(`/contacts/${id}/notes`, { note }); setNote(""); toast.success("Note added"); load(); };
  const logActivity = async (type) => { await api.post(`/contacts/${id}/activities`, { type, title: `${type} logged` }); toast.success(`${type} logged`); load(); };
  const changeStage = async (stage) => { await api.patch(`/contacts/${id}`, { stage }); toast.success("Stage updated"); load(); };
  const aiSummary = async () => { setLoadingAi(true); try { const { data } = await api.post(`/ai/summarize-contact/${id}`); setSummary(data.summary); } catch { toast.error("AI unavailable"); } finally { setLoadingAi(false); } };

  if (!d) return <div className="text-slate-400 text-sm">Loading…</div>;
  const c = d.contact;

  return (
    <div>
      <Link to="/app/contacts" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5 mb-4"><ArrowLeft size={15} /> Contacts</Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-alto-black text-white flex items-center justify-center font-display font-bold text-xl">{(c.first_name || "?").slice(0,1)}</div>
              <div>
                <h1 className="font-display text-xl font-bold text-slate-900" data-testid="contact-name">{c.first_name} {c.last_name}</h1>
                <Badge tone="red">{c.status}</Badge>
              </div>
            </div>
            <div className="mt-5 space-y-2 text-sm">
              {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-alto-red"><Phone size={14} />{c.phone}</a>}
              {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-slate-700 hover:text-alto-red break-all"><Mail size={14} />{c.email}</a>}
              {(c.city || c.state) && <p className="text-slate-500">{[c.city, c.state].filter(Boolean).join(", ")}</p>}
            </div>
            <div className="flex gap-2 mt-5">
              {c.phone && <a href={`tel:${c.phone}`} className="flex-1 bg-slate-100 hover:bg-slate-200 rounded-md py-2.5 flex items-center justify-center text-slate-600" title="Call"><Phone size={16} /></a>}
              {c.phone && <a href={`sms:${c.phone}`} className="flex-1 bg-slate-100 hover:bg-slate-200 rounded-md py-2.5 flex items-center justify-center text-slate-600" title="Text"><MessageSquare size={16} /></a>}
              {c.email && <a href={`mailto:${c.email}`} className="flex-1 bg-slate-100 hover:bg-slate-200 rounded-md py-2.5 flex items-center justify-center text-slate-600" title="Email"><Mail size={16} /></a>}
            </div>
          </Card>

          <Card className="p-6 space-y-3 text-sm">
            <h3 className="font-display font-bold text-slate-900">Attribution</h3>
            <Row label="Original Source" value={c.original_source} />
            <Row label="Original Campaign" value={c.original_campaign} />
            <Row label="Latest Source" value={c.latest_source} />
            <Row label="Latest Campaign" value={c.latest_campaign} />
            {c.utm && Object.keys(c.utm).length > 0 && <div className="pt-2 border-t border-slate-100"><p className="text-xs text-slate-400">UTM: {Object.entries(c.utm).map(([k,v]) => `${k}=${v}`).join(" · ")}</p></div>}
          </Card>

          <Card className="p-6">
            <h3 className="font-display font-bold text-slate-900 mb-3">Pipeline Stage</h3>
            <select value={c.stage || ""} onChange={(e) => changeStage(e.target.value)} data-testid="contact-stage-select" className={inputCls}>
              <option value="">— Select stage —</option>
              {(pipeline?.stages || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {/* Actions */}
          <Card className="p-5">
            <div className="flex flex-wrap gap-2">
              <GhostButton onClick={() => logActivity("Phone Call")} data-testid="log-call"><Phone size={14} /> Log Call</GhostButton>
              <GhostButton onClick={() => logActivity("Text")} data-testid="log-text"><MessageSquare size={14} /> Log Text</GhostButton>
              <GhostButton onClick={() => logActivity("Email")} data-testid="log-email"><Mail size={14} /> Log Email</GhostButton>
              <RedButton onClick={() => setShowTask(true)} data-testid="add-followup"><Plus size={14} /> Follow-Up</RedButton>
              <GhostButton onClick={aiSummary} disabled={loadingAi} data-testid="ai-summary"><Sparkles size={14} /> {loadingAi ? "Summarizing…" : "AI Summary"}</GhostButton>
            </div>
            {summary && <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="ai-summary-text"><div className="flex items-center gap-1.5 text-xs font-semibold text-alto-red mb-1.5"><Sparkles size={12} /> AI-GENERATED SUMMARY</div><p className="text-sm text-slate-700">{summary}</p></div>}
          </Card>

          {/* Tasks */}
          {d.tasks.filter((t) => t.status === "Pending").length > 0 && (
            <Card className="p-5">
              <h3 className="font-display font-bold text-slate-900 mb-3 flex items-center gap-2"><Clock size={16} /> Open Follow-Ups</h3>
              <div className="space-y-2">
                {d.tasks.filter((t) => t.status === "Pending").map((t) => (
                  <div key={t.id} className="flex items-center justify-between bg-slate-50 rounded-md p-3" data-testid={`contact-task-${t.id}`}>
                    <div><p className="text-sm font-medium text-slate-800">{t.task_type} · {t.description}</p><p className="text-xs text-slate-500">Due {t.due_date?.slice(0,10)}</p></div>
                    <RedButton className="!py-1.5 !px-3" onClick={async () => { await api.post(`/tasks/${t.id}/complete`, {}); toast.success("Completed"); load(); }}>Complete</RedButton>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Add note */}
          <Card className="p-5">
            <div className="flex gap-2">
              <input className={inputCls} placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="note-input" />
              <RedButton onClick={addNote} data-testid="note-save">Add</RedButton>
            </div>
          </Card>

          {/* Timeline */}
          <Card className="p-6">
            <h3 className="font-display font-bold text-slate-900 mb-5">Relationship Timeline</h3>
            {d.activities.length === 0 ? <p className="text-sm text-slate-400">No activity yet.</p> : (
              <div className="space-y-5">
                {d.activities.map((a) => {
                  const Ic = ICONS[a.type] || StickyNote;
                  return (
                    <div key={a.id} className="flex gap-4" data-testid={`activity-${a.id}`}>
                      <div className="shrink-0 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><Ic size={15} /></div>
                      <div className="flex-1 pb-5 border-b border-slate-100 last:border-0 -mt-1">
                        <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-slate-800">{a.title}</span><span className="text-xs text-slate-400 shrink-0">{a.created_at?.slice(0,10)}</span></div>
                        <Badge>{a.type}</Badge>
                        {a.detail && <p className="text-sm text-slate-600 mt-1.5">{a.detail}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>

      {showTask && <TaskModal contactId={id} onClose={() => setShowTask(false)} onSaved={() => { setShowTask(false); load(); }} />}
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between gap-3"><span className="text-slate-500">{label}</span><span className="text-slate-800 font-medium text-right">{value || "—"}</span></div>;
}

function TaskModal({ contactId, onClose, onSaved }) {
  const [form, setForm] = useState({ contact_id: contactId, task_type: "Call", description: "Follow up", due_date: new Date(Date.now() + 86400000).toISOString().slice(0,10), priority: "Normal" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => { try { await api.post("/tasks", { ...form, due_date: new Date(form.due_date).toISOString() }); toast.success("Follow-up scheduled"); onSaved(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-xl font-bold text-slate-900 mb-5">Schedule Follow-Up</h2>
        <div className="space-y-3">
          <Field label="Type"><select className={inputCls} value={form.task_type} onChange={set("task_type")} data-testid="task-type">{["Call","Text","Email","Add Note","Meeting"].map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Description"><input className={inputCls} value={form.description} onChange={set("description")} data-testid="task-desc" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Due date"><input type="date" className={inputCls} value={form.due_date} onChange={set("due_date")} data-testid="task-due" /></Field>
            <Field label="Priority"><select className={inputCls} value={form.priority} onChange={set("priority")} data-testid="task-priority">{["Low","Normal","High","Urgent"].map((p) => <option key={p}>{p}</option>)}</select></Field>
          </div>
        </div>
        <div className="flex gap-3 mt-6"><GhostButton className="flex-1" onClick={onClose}>Cancel</GhostButton><RedButton className="flex-1" onClick={save} data-testid="task-save">Schedule</RedButton></div>
      </Card>
    </div>
  );
}
